import { InspectionCreateParams } from 'entities/GraphQL/InspectionCreateParams';
import { CategoryResultPayloadStatus } from 'entities/attributes/InspectionCategoryResultPayloadJSONAttributes';
import { InspectionUpdateParams } from 'entities/GraphQL/InspectionUpdateParams';
import { IChecklistAnswer } from 'entities/IChecklistAnswer';
import {
  createInspectionMutation,
  inspectionResultCreateMutation,
  inspectionResetMutation,
  categoriesResultSetStatusMutation,
  categoriesResultRemoveStatusMutation,
  categoryResultUpsertMutation,
  inspectionResultRemoveMutation,
  updateInspectionMutation,
  inspectionResultUpdateMutation,
  answerChecklistQuestions,
} from 'store/mpi/mutations';
import {
  getInspectionTypesQuery,
  getInspectionQuery,
  getInspectionByRepairRequestIdQuery,
  getInspectionTypesByTypeQuery,
  getInspectionTypeByIdQuery,
  getChecklist,
  getChecklistQuery,
} from 'store/mpi/queries';
import createAction from 'store/utils/createAction';
import createAPIAction from 'store/utils/createAPIAction';
import { CreateInspectionResultInput } from 'entities/GraphQL/CreateInspectionResultInput';
import { UpsertInspectionCategoryResultInput } from 'entities/GraphQL/UpsertInspectionCategoryResultInput';
import { UpdateInspectionResultInput } from 'entities/GraphQL/UpdateInspectionResultInput';

const serviceName = 'mpi';
export const graphQLService = '/mpi-service/graphql';

export const getInspectionTypes = createAPIAction(
  `${serviceName}/GET_INSPECTION_TYPES`,
  () => ({
    endpoint: graphQLService,
    method: 'POST',
    data: getInspectionTypesQuery,
  })
);

export const getChecklistType = createAPIAction(
  `${serviceName}/GET_CHECKLIST_TYPE`,
  (data) => ({
    endpoint: graphQLService,
    method: 'POST',
    data: getChecklist(data),
  })
);

export const getInspectionTypesByType = createAPIAction(
  `${serviceName}/GET_INSPECTION_TYPES_BY_TYPE`,
  (data) => ({
    endpoint: graphQLService,
    method: 'POST',
    data: getInspectionTypesByTypeQuery(data),
  })
);

export const getInspectionTypeById = createAPIAction(
  `${serviceName}/GET_INSPECTION_TYPE_BY_ID`,
  (data) => ({
    endpoint: graphQLService,
    method: 'POST',
    data: getInspectionTypeByIdQuery(data),
  })
);

export const getInspection = createAPIAction(
  `${serviceName}/GET_INSPECTION`,
  (data) => ({
    endpoint: graphQLService,
    method: 'POST',
    data: getInspectionQuery(data),
  })
);

export const getInspectionChecklist = createAPIAction(
  `${serviceName}/GET_INSPECTION_CHECKLIST`,
  (data) => ({
    endpoint: graphQLService,
    method: 'POST',
    data: getChecklistQuery(data),
  })
);

export const getInspectionByRepairRequestId = createAPIAction(
  `${serviceName}/GET_INSPECTION_BY_REPAIR_REQUEST_ID`,
  (data) => ({
    endpoint: graphQLService,
    method: 'POST',
    data: getInspectionByRepairRequestIdQuery(data),
  })
);

export const createInspection = createAPIAction(
  `${serviceName}/CREATE_INSPECTION`,
  (data: InspectionCreateParams) => ({
    endpoint: graphQLService,
    method: 'POST',
    data: createInspectionMutation(data),
  })
);

export const updateInspection = createAPIAction(
  `${serviceName}/UPDATE_INSPECTION`,
  (data: InspectionUpdateParams) => ({
    endpoint: '/mpi-service/graphql',
    method: 'POST',
    data: updateInspectionMutation(data),
  })
);

export const createInspectionResult = createAPIAction(
  `${serviceName}/CREATE_INSPECTION_RESULT`,
  (data: CreateInspectionResultInput) => ({
    endpoint: '/mpi-service/graphql',
    method: 'POST',
    data: inspectionResultCreateMutation(data),
  })
);

export const updateInspectionResult = createAPIAction(
  `${serviceName}/UPDAtE_INSPECTION_RESULT`,
  (data: UpdateInspectionResultInput) => ({
    endpoint: '/mpi-service/graphql',
    method: 'POST',
    data: inspectionResultUpdateMutation(data),
  })
);

export const removeInspectionResult = createAPIAction(
  `${serviceName}/REMOVE_INSPECTION_RESULT`,
  (inspectionResultId: string) => ({
    endpoint: '/mpi-service/graphql',
    method: 'POST',
    data: inspectionResultRemoveMutation(inspectionResultId),
  })
);

export const inspectionReset = createAPIAction(
  `${serviceName}/INSPECTION_RESET`,
  ({ id }: { id: string }) => ({
    endpoint: graphQLService,
    method: 'POST',
    data: inspectionResetMutation(id),
  })
);

export const categoriesResultSetStatus = createAPIAction(
  `${serviceName}/CATEGORY_RESULT_SET_STATUS`,
  ({
    idInspection,
    idCategory,
    status,
  }: {
    idInspection: string;
    idCategory: string;
    status: CategoryResultPayloadStatus;
  }) => ({
    endpoint: graphQLService,
    method: 'POST',
    data: categoriesResultSetStatusMutation(idInspection, idCategory, status),
  })
);

export const categoryResultUpsert = createAPIAction(
  `${serviceName}/CATEGORY_RESULT_UPSERT_STATUS`,
  (categoryResult: UpsertInspectionCategoryResultInput) => ({
    endpoint: graphQLService,
    method: 'POST',
    data: categoryResultUpsertMutation(categoryResult),
  })
);

export const categoriesResultRemoveStatus = createAPIAction(
  `${serviceName}/CATEGORY_RESULT_REMOVE_STATUS`,
  ({
    idInspection,
    idCategory,
  }: {
    idInspection: string;
    idCategory: string;
  }) => ({
    endpoint: graphQLService,
    method: 'POST',
    data: categoriesResultRemoveStatusMutation(idInspection, idCategory),
  })
);

export const uploadInspectionPhoto = createAPIAction<
  [{ inspectionId: string; files: File[]; userId: string }]
>(`${serviceName}/UPLOAD_INVOICE`, ({ inspectionId, files, userId }) => {
  const formData = new FormData();
  formData.append('folderPath', `mpi-documents/inspections/${inspectionId}`);
  formData.append('thumbnail', 'true');
  formData.append('userId', userId);
  formData.append('documents', files[0]);

  return {
    endpoint: '/lambda/documentUpload',
    method: 'POST',
    data: formData,
  };
});

export const clearInspection = createAction(`${serviceName}/CLEAR_INSPECTION`);

export const setInspectionCategoryIdAnchor = createAction(
  `${serviceName}/SET_INSPECTION_CATEGORY_ANCHOR`
);

export const setChecklistQuestionAnswer = createAction(
  `${serviceName}/SET_CHECKLIST_QUESTION_ANSWER`
);

export const answerQuestionsUpsert = createAPIAction(
  `${serviceName}/ANSWER_QUESTIONS_UPSERT`,
  (answers: IChecklistAnswer[]) => ({
    endpoint: graphQLService,
    method: 'POST',
    data: answerChecklistQuestions(answers),
  })
);

export const sendDotBitEmail = createAPIAction<
  [
    {
      emailType: 'FAIL' | 'SUCCESS';
      toEmails: string[];
      vin: string;
      pdfUrl: string;
    }
  ]
>(`${serviceName}/SEND_DOT_BIT_EMAIL`, (params) => ({
  endpoint: '/isp-service/admin/email/dotbit',
  method: 'POST',
  data: {
    emailType: params.emailType,
    toEmails: params.toEmails,
    vin: params.vin,
    attachments: [
      {
        url: params.pdfUrl,
        name: params.pdfUrl.substring(params.pdfUrl.lastIndexOf('/') + 1),
      },
    ],
  },
}));
